import React from 'react';
import styled from 'styled-components';

const features = [
  {
    icon: '🏛️',
    title: 'Employer Brand Page',
    desc: 'Permanent visibility within the Sainoo ecosystem — not only when you are actively recruiting.',
    size: 'full',
  },
  {
    icon: '🎯',
    title: 'Multi-university Distribution',
    desc: 'Distribute roles across 60+ European universities in one click. Target the schools that match each mandate.',
    size: 'large',
  },
  {
    icon: '⚡',
    title: 'Integrated Corporate Finance Assessment',
    desc: 'Corporate Finance tests covering LBO, valuation and M&A. Automatically scored and structured.',
    size: 'large',
  },
  {
    icon: '📊',
    title: 'Customisable ATS Pipeline',
    desc: 'Structured stages adapted to your internal hiring workflow. Centralised and collaborative.',
    size: 'small',
  },
  {
    icon: '💬',
    title: 'Candidate Feedback System',
    desc: 'Structured ratings and real-time experience data to improve each recruitment cycle.',
    size: 'small',
  },
  {
    icon: '🤖',
    title: 'Automation & AI',
    desc: 'Automated updates and intelligent candidate communication so your team focuses on decisions.',
    size: 'small',
  },
];

const SectionWrapper = styled.section`
  background: #f8fafc;
  padding: 120px 5%;
  position: relative;
  overflow: hidden;
`;

const DecorativeBlob = styled.div<{ top?: string; left?: string; right?: string; bottom?: string; color: string }>`
  position: absolute;
  top: ${(props) => props.top || 'auto'};
  left: ${(props) => props.left || 'auto'};
  right: ${(props) => props.right || 'auto'};
  bottom: ${(props) => props.bottom || 'auto'};
  width: 400px;
  height: 400px;
  background: ${(props) => props.color};
  filter: blur(100px);
  opacity: 0.15;
  pointer-events: none;
  z-index: 0;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const CategoryLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  &::before {
    content: '';
    width: 24px;
    height: 2px;
    background: #2dbd96;
  }

  span {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #2dbd96;
    font-family: 'DM Sans', sans-serif;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'DM Sans', sans-serif;
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 700;
  line-height: 1.1;
  color: #0d1b2a;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
`;

const SectionDesc = styled.p`
  font-size: 1.15rem;
  color: #475569;
  line-height: 1.7;
  max-width: 600px;
  margin-bottom: 72px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-auto-rows: minmax(280px, auto);
  gap: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }
`;

const FeatureCard = styled.div<{ size?: string }>`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 24px;
  padding: 48px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  grid-column: ${(props) => (props.size === 'full' ? 'span 6' : props.size === 'large' ? 'span 3' : 'span 2')};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  position: relative;

  @media (max-width: 1100px) {
    grid-column: span 2;
    padding: 32px;
  }
  @media (max-width: 768px) {
    grid-column: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 48px rgba(0, 0, 0, 0.08);
    border-color: rgba(45, 189, 150, 0.3);

    .icon-bg {
      transform: scale(1.2) rotate(5deg);
      background: rgba(45, 189, 150, 0.15);
    }
  }
`;

const IconWrapper = styled.div.attrs({ className: 'icon-bg' })`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  font-size: 1.8rem;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
  transition: all 0.4s ease;
`;

const FeatureTitle = styled.h3`
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0d1b2a;
  margin-bottom: 16px;
  letter-spacing: -0.01em;
`;

const FeatureDesc = styled.p`
  font-size: 1rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
`;

const EmployerFeatures = () => {
  return (
    <SectionWrapper id="features">
      <DecorativeBlob color="#2dbd96" top="-100px" right="5%" />
      <DecorativeBlob color="#0277bd" bottom="-100px" left="5%" />

      <ContentContainer>
        <CategoryLabel>
          <span>Platform features</span>
        </CategoryLabel>

        <SectionTitle>Built for speed. Scaled for excellence.</SectionTitle>

        <SectionDesc>
          A unified workspace designed to handle the complexities of high-volume Corporate Finance hiring without
          sacrificing precision.
        </SectionDesc>

        <FeatureGrid>
          {features.map((feature) => (
            <FeatureCard key={feature.title} size={feature.size}>
              <IconWrapper className="icon-bg">{feature.icon}</IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDesc>{feature.desc}</FeatureDesc>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </ContentContainer>
    </SectionWrapper>
  );
};

export default EmployerFeatures;
